import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { catchError, noop, of } from 'rxjs';

import { ObjectsManagerActions } from '../../state/actions';
import { ObjectsManagerState } from '../../state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { AccountService } from 'app/core';
import { FeatureFlag } from '@core/enums';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'add-relationship-to-object',
  templateUrl: './add-relationship-to-object.component.html',
  styleUrls: ['./add-relationship-to-object.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRelationshipToObjectComponent extends OnDestroyMixin implements OnInit {
  addRelationshipToObjectForm: FormGroup;
  customObject: CustomObject;
  customObjectOptions: CustomObject[];
  isLoading: boolean;
  isInverseRelationshipOn$ = this.store.select(AccountState.isFeatureEnabled(FeatureFlag.INVERSE_RELATIONSHIP));

  public get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.addRelationshipToObjectForm.pristine || this.addRelationshipToObjectForm.invalid;
  }

  constructor(
    public matDialogRef: MatDialogRef<AddRelationshipToObjectComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly snackBar: MatSnackBar,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.customObject = this.store.selectSnapshot(ObjectsManagerState.getById);
    this.buildForm();
    this.getCustomObjectOptions();

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionSuccessful(ObjectsManagerActions.CreateLink))
      .subscribe(() => {
        this.isLoading = false;
        this.snackBar.open('Object relationship has been created.', '', {
          duration: 3000,
        });
        this.close();
      });
  }

  private buildForm(): void {
    this.addRelationshipToObjectForm = this.formBuilder.group({
      linked_type: ['', [Validators.required]],
      relationship_type: ['', [Validators.required]],
    });
  }

  getCustomObjectOptions(): void {
    const customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
    const currentLinkedTypes = this.customObject.links?.map(({ linked_type }) => linked_type) ?? [];
    this.customObjectOptions = customObjects?.filter((customObject) => {
      return (
        customObject?.linkable &&
        !currentLinkedTypes.includes(customObject.name) &&
        customObject.name !== this.customObject.name
      );
    });
  }

  isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  get linkedObject(): CustomObject {
    const customObjectOption = this.customObjectOptions.find(
      (option) => option.name === this.addRelationshipToObjectForm.get('linked_type').value,
    );
    return customObjectOption;
  }

  saveAndClose(): void {
    this.addRelationshipToObjectForm.disable();
    this.isLoading = true;
    const newRelationship = { ...this.addRelationshipToObjectForm.value };

    if (this.customObject.id) {
      newRelationship['custom_object_id'] = this.customObject.id;
    } else {
      newRelationship['related_to_type'] = this.customObject.name;
    }

    this.store
      .dispatch(new ObjectsManagerActions.CreateLink(newRelationship))
      .pipe(
        untilComponentDestroyed(this),
        catchError((response) => {
          const errorMessages = ['[ERROR]: Error creating relationship.'];
          const additionalErrorMessages = response.error?.link?.errors;
          if (Array.isArray(additionalErrorMessages)) {
            errorMessages.push(...additionalErrorMessages);
          }
          this.isLoading = false;
          this.snackBar.open(errorMessages.join(' '), '', {
            duration: 3000,
          });
          this.close();
          return of();
        }),
      )
      .subscribe(() => {
        this.accountService.fieldDefsCacheBuster();
      });
  }

  close(): void {
    this.matDialogRef.close();
  }

  filterObjects(value: CustomObject[], isInverseRelationshipOn: boolean): CustomObject[] {
    if (!isInverseRelationshipOn) {
      return value.filter((x) => x.id);
    }

    return value;
  }
}
